import React, { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Switch, Route } from "react-router-dom";
import "./App.scss";
import LegacyEntry from "./pages/LegacyEntry";
import ComplianceSessionPage from "./pages/ComplianceSessionPage";
import LanguageInit from "./language";

/**
 * Suppress non-critical runtime error from viamobi-component's
 * PhotoCaptureIdVerification: a setTimeout tries to .click() an element
 * that may no longer be in the DOM when the KYC form replaces the capture view.
 * The real fix belongs in the package (optional chaining on getElementById).
 */
const isKnownViamobiError = (msg) =>
  typeof msg === 'string' &&
  msg.includes("Cannot read properties of null (reading 'click')");

window.addEventListener('error', (event) => {
  if (isKnownViamobiError(event.message)) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return true;
  }
});

/**
 * Sanitize a query parameter value by removing HTML tags, script content,
 * and common injection patterns. Returns empty string for null/undefined.
 */
const sanitizeParam = (value) => {
  if (value == null) return null;
  return value
    .replace(/<[^>]*>/g, '')        // Strip HTML tags
    .replace(/javascript:/gi, '')    // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '')      // Remove event handlers
    .replace(/['";]/g, '')           // Remove quotes and semicolons
    .trim();
};

/** Sanitize and constrain to a max length. Returns empty string for null. */
const sanitizeWithDefault = (value, maxLength = 255) => {
  const sanitized = sanitizeParam(value);
  if (sanitized == null) return '';
  return sanitized.substring(0, maxLength);
};

const queryParams = new URLSearchParams(window.location.search);
const option = sanitizeParam(queryParams.get("opt"));
const agency = sanitizeParam(queryParams.get("agency"));
const order = sanitizeParam(queryParams.get("order"));
const amount = sanitizeParam(queryParams.get("amount"));
let lang = sanitizeParam(queryParams.get("lang"));
const idSenderGlobal = sanitizeParam(queryParams.get("senderG"));
const nameSender = sanitizeParam(queryParams.get("nameSender"));
const iddoc = sanitizeParam(queryParams.get("iddoc"));
const environment = sanitizeParam(queryParams.get("e"));
const region = sanitizeParam(queryParams.get("r"));
const iris = sanitizeParam(queryParams.get("i"));
const selfie = sanitizeParam(queryParams.get("se"));
const idShortener = sanitizeParam(queryParams.get("idS"));
const isBackCapture = sanitizeParam(queryParams.get("bk"));
const documentTypeId = sanitizeParam(queryParams.get("dt"));
const username = sanitizeWithDefault(queryParams.get("username"));
const reqsource = sanitizeWithDefault(queryParams.get("reqsource"));
const transactionUuid = sanitizeParam(queryParams.get("txUuid"));
const idOnly = sanitizeParam(queryParams.get("idonly"));


if (lang) {
  if (lang !== "en" && lang !== "es") {
    lang = "en";
    localStorage.setItem("i18nextLng", lang);
  } else {
    localStorage.setItem("i18nextLng", lang);
  }
} else {
  const localStorageLanguage = localStorage.getItem("i18nextLng");

  if (localStorageLanguage !== "en" && localStorageLanguage !== "es") {
    localStorage.setItem("i18nextLng", "en");
    lang = "en";
  } else {
    lang = localStorageLanguage;
  }
}

const root = createRoot(document.getElementById("root"));
root.render(
  <Suspense fallback={<div>Loading...</div>}>
    <React.StrictMode>
      <LanguageInit />
      <BrowserRouter>
        <Switch>
          {/* Opaque session entry — URL carries only the session ID */}
          <Route path="/compliance/:sessionId" component={ComplianceSessionPage} />
          {/* Legacy query-string entry — auto-cloaks the URL to /compliance/{id} */}
          <Route path="/">
            <LegacyEntry
              optionQS={option}
              agencyQS={agency}
              orderQS={order}
              amountQS={amount}
              language={lang}
              idSenderGlobal={idSenderGlobal}
              nameSender={nameSender}
              iddoc={iddoc}
              env={environment}
              region={region}
              iris={iris}
              selfie={selfie}
              idShortener={idShortener}
              isBackCapture={isBackCapture}
              documentTypeId={documentTypeId}
              username={username}
              reqsource={reqsource}
              transactionUuid={transactionUuid}
              idOnly={idOnly}
            />
          </Route>
        </Switch>
      </BrowserRouter>
    </React.StrictMode>
  </Suspense>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
//reportWebVitals();
